import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { UnifiedContactModal } from "./UnifiedContactModal";

// ─────────────────────────────────────────────────────────────────────────────
// ContactModalProvider — one shared READ-ONLY contact modal for every surface
// OUTSIDE the campaign hub + campaign tasks (list builder, account contact tabs,
// company roster, targeting, pipeline rows). It always opens in mode="default"
// with NO comms panel — pure profile + activity. The campaign/task comms modal
// stays separate (those surfaces mount their own UnifiedContactModal with a panel).
// Standard: launch-platform/template/modules/contact_modal.md §3.
// ─────────────────────────────────────────────────────────────────────────────

export interface ContactModalFallback {
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  job_title?: string | null;
  company_name?: string | null;
  email?: string | null;
  linkedin_url?: string | null;
  location?: string | null;
  headline?: string | null;
}

export interface OpenContactArgs {
  /** contacts.id — hydrates the full read-only profile. May be null for unmatched contacts. */
  contactId: string | null;
  /** Shown when there's no matched contact row. */
  fallbackData?: ContactModalFallback | null;
}

interface ContactModalContextValue {
  openContact: (args: OpenContactArgs) => void;
}

const ContactModalContext = createContext<ContactModalContextValue | null>(null);

export function ContactModalProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [contactId, setContactId] = useState<string | null>(null);
  const [fallbackData, setFallbackData] = useState<ContactModalFallback | null>(null);

  const openContact = useCallback((args: OpenContactArgs) => {
    setContactId(args.contactId);
    setFallbackData(args.fallbackData ?? null);
    setOpen(true);
  }, []);

  const value = useMemo(() => ({ openContact }), [openContact]);

  return (
    <ContactModalContext.Provider value={value}>
      {children}
      <UnifiedContactModal
        contactId={contactId}
        open={open}
        onOpenChange={setOpen}
        mode="default"
        fallbackData={fallbackData}
      />
    </ContactModalContext.Provider>
  );
}

/**
 * Open the shared read-only contact modal from anywhere under the provider.
 * Returns a no-op opener if no provider is mounted (so call sites stay safe).
 */
export function useContactModal(): ContactModalContextValue {
  const ctx = useContext(ContactModalContext);
  return ctx ?? { openContact: () => { /* no provider mounted */ } };
}
